import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);
const TOKEN_KEY = "queensway_token";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setAuth = useCallback((tok, usr) => {
    if (tok) {
      localStorage.setItem(TOKEN_KEY, tok);
      setToken(tok);
    }
    if (usr) setUser(usr);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      setUser(res.data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setAuth(res.data.access_token, res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password, date_of_birth) => {
    const payload = { name, email, password };
    if (date_of_birth) payload.date_of_birth = date_of_birth;
    const res = await axios.post(`${API}/auth/register`, payload);
    setAuth(res.data.access_token, res.data.user);
    return res.data.user;
  };

  const updateProfile = async (updates) => {
    const t = localStorage.getItem(TOKEN_KEY);
    const res = await axios.patch(`${API}/auth/me`, updates, { headers: { Authorization: `Bearer ${t}` } });
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refresh, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Helper for authenticated axios
export function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}
